import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Zap, Search, X, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PostCard } from '../components/PostCard';
import { ProjectsList } from './ProjectsIndex';
import { SEO } from '../components/SEO';
import { useSite } from '../contexts/SiteContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function HomePage() {
  const [posts, setPosts] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const { settings, pages } = useSite();

  const view = searchParams.get('view') === 'projects' ? 'projects' : 'posts';
  const setView = (v) => {
    const p = new URLSearchParams(searchParams);
    if (v === 'projects') p.set('view', 'projects');
    else p.delete('view');
    setSearchParams(p);
  };

  const searchQuery = searchParams.get('q') || '';
  const categoryFilter = searchParams.get('category') || '';
  const tagFilter = searchParams.get('tag') || '';

  const [searchInput, setSearchInput] = useState(searchQuery);

  useEffect(() => {
    fetchPosts();
    fetchTags();
    fetchCategories();
  }, [searchQuery, categoryFilter, tagFilter]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('q', searchQuery);
      if (categoryFilter) params.set('category', categoryFilter);
      if (tagFilter) params.set('tag', tagFilter);

      const url = params.toString() 
        ? `${API}/search?${params.toString()}`
        : `${API}/posts`;

      const response = await axios.get(url);
      setPosts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setAllTags(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchInput) {
      params.set('q', searchInput);
    } else {
      params.delete('q');
    }
    setSearchParams(params);
  };

  const handleCategoryClick = (category) => {
    const params = new URLSearchParams(searchParams);
    if (categoryFilter === category) {
      params.delete('category');
    } else {
      params.set('category', category);
    }
    setSearchParams(params);
  };

  const handleTagClick = (tag) => {
    const params = new URLSearchParams(searchParams);
    if (tagFilter === tag) {
      params.delete('tag');
    } else {
      params.set('tag', tag);
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || categoryFilter || tagFilter;

  return (
    <div>
      <SEO
        title={null}
        description={settings.site_tagline}
        url="/"
      />
      {/* Hero Section */}
      <section 
        className="relative py-10 px-4 sm:px-6 lg:px-8 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/11145690/pexels-photo-11145690.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt=""
            className="w-full h-full object-cover"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="max-w-2xl animate-fade-in">
            <div className="flex items-center gap-2 text-primary mb-4">
              <Zap className="h-5 w-5" />
              <span className="text-xs tracking-[0.2em] uppercase font-mono font-medium">
                DIY • Tech • Solar
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-4 text-balance">
              {settings.site_name}
            </h1>
            
            <p className="text-lg text-muted-foreground mb-8">
              {settings.site_tagline}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="hero-contact-btn">
                  Get in Touch
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#latest-posts" onClick={() => setView('posts')}>
                <Button variant="outline" data-testid="hero-posts-btn">
                  View Posts
                </Button>
              </a>
              <a href="#latest-posts" onClick={() => setView('projects')}>
                <Button variant="outline" data-testid="hero-projects-btn">
                  Browse Projects
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Filter Section */}
      {view === 'posts' && (
      <section className="py-8 px-4 sm:px-6 lg:px-8 border-b border-border">
        <div className="max-w-6xl mx-auto">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-2 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search posts..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" data-testid="search-btn">Search</Button>
          </form>

          {/* Categories */}
          {categories.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Categories</h3>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Button
                    key={cat}
                    variant={categoryFilter === cat ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleCategoryClick(cat)}
                    data-testid={`category-${cat}`}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {allTags.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <Badge
                    key={tag.name}
                    variant={tagFilter === tag.name ? "default" : "secondary"}
                    className="cursor-pointer hover:bg-primary/80"
                    onClick={() => handleTagClick(tag.name)}
                    data-testid={`tag-${tag.name}`}
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {tag.name} ({tag.count})
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 pt-2 border-t border-border mt-4">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {searchQuery && (
                <Badge variant="outline">
                  Search: {searchQuery}
                  <button onClick={() => { setSearchInput(''); const p = new URLSearchParams(searchParams); p.delete('q'); setSearchParams(p); }} className="ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {categoryFilter && (
                <Badge variant="outline">
                  Category: {categoryFilter}
                  <button onClick={() => handleCategoryClick(categoryFilter)} className="ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {tagFilter && (
                <Badge variant="outline">
                  Tag: {tagFilter}
                  <button onClick={() => handleTagClick(tagFilter)} className="ml-1">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Latest Posts / Projects */}
      <section 
        id="latest-posts" 
        className="py-16 px-4 sm:px-6 lg:px-8"
        data-testid="posts-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                {view === 'projects'
                  ? 'Projects'
                  : (hasActiveFilters ? 'Search Results' : 'Latest Posts')}
              </h2>
              <p className="text-muted-foreground mt-1">
                {view === 'projects'
                  ? 'Browse every project page, grouped by topic'
                  : (hasActiveFilters
                      ? `Found ${posts.length} post${posts.length !== 1 ? 's' : ''}`
                      : 'Recent projects, tutorials, and thoughts')}
              </p>
            </div>

            {/* Posts / Projects toggle */}
            <div className="inline-flex rounded-md border border-border overflow-hidden" role="group" aria-label="Toggle posts or projects">
              <button
                type="button"
                onClick={() => setView('posts')}
                aria-pressed={view === 'posts'}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  view === 'posts' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
                data-testid="view-toggle-posts"
              >
                Posts
              </button>
              <button
                type="button"
                onClick={() => setView('projects')}
                aria-pressed={view === 'projects'}
                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-border ${
                  view === 'projects' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
                data-testid="view-toggle-projects"
              >
                Projects
              </button>
            </div>
          </div>

          {view === 'projects' ? (
            <ProjectsList pages={pages} />
          ) : loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border border-border rounded-lg overflow-hidden animate-pulse">
                  <div className="aspect-video bg-muted" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-muted rounded w-1/4" />
                    <div className="h-6 bg-muted rounded w-3/4" />
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (Array.isArray(posts) && posts.length > 0) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <PostCard key={post.id} post={post} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-dashed border-border rounded-lg">
              <p className="text-muted-foreground">
                {hasActiveFilters 
                  ? 'No posts found matching your criteria.' 
                  : 'No posts yet. Check back soon!'}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" className="mt-4" onClick={clearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Solar Projects</h3>
              <p className="text-sm text-muted-foreground">
                Off-grid setups, battery builds, and renewable energy experiments.
              </p>
            </div>
            
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg mb-2">DIY Electronics</h3>
              <p className="text-sm text-muted-foreground">
                From battery packs to custom PCBs, hands-on electronics projects.
              </p>
            </div>
            
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg mb-2">Tech Insights</h3>
              <p className="text-sm text-muted-foreground">
                Reviews, tutorials, and deep dives into technology.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
